/**
 * O manifesto vivo da sessão — T18 da spec #408.
 *
 * Trocar de contexto é gravar a escolha e pedir o manifesto de novo. Nada de
 * `window.location.reload()`: o menu, as guardas e os módulos saem todos daqui, então
 * substituir o manifesto já refaz a interface inteira sem descartar a página.
 *
 * Não persiste em `localStorage` de propósito. O contexto ativo mora no servidor (T4c) — uma
 * cópia local sobreviveria a um vínculo revogado e mostraria menu de um contexto que a pessoa
 * não alcança mais.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { contextosDaRecusa, manifestoService } from '../services/manifestoService';
import type { ContextoAtivo, ContextoDisponivel, Manifesto } from '../types/manifesto';
import { clearOnboardingCache } from '../hooks/onboardingCache';
import { clearPrerequisiteCache } from '../hooks/usePrerequisites';

interface ManifestoState {
  manifesto: Manifesto | null;
  /**
   * As opções que o servidor devolveu ao recusar por falta de contexto ativo — vazia quando
   * a pessoa não tem vínculo nenhum. Não é erro: é a entrada pela tela de escolha (T3).
   */
  escolhaPendente: ContextoDisponivel[] | null;
  isLoading: boolean;
  erro: string | null;
}

interface ManifestoActions {
  carregar: () => Promise<void>;
  trocarContexto: (contexto: ContextoAtivo) => Promise<void>;
  limpar: () => void;
}

const initialState: ManifestoState = {
  manifesto: null,
  escolhaPendente: null,
  isLoading: false,
  erro: null,
};

export const useManifestoStore = create<ManifestoState & ManifestoActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      carregar: async () => {
        if (get().isLoading) return;
        set({ isLoading: true, erro: null });
        try {
          const manifesto = await manifestoService.obter();
          set({ manifesto, escolhaPendente: null, isLoading: false });
        } catch (error) {
          const escolhaPendente = contextosDaRecusa(error);
          if (escolhaPendente) {
            set({ escolhaPendente, manifesto: null, isLoading: false, erro: null });
            return;
          }
          set({
            isLoading: false,
            erro: error instanceof Error ? error.message : 'Não foi possível carregar o acesso',
          });
        }
      },

      trocarContexto: async (contexto: ContextoAtivo) => {
        set({ isLoading: true, erro: null });
        try {
          await manifestoService.definirContexto(contexto);
          const manifesto = await manifestoService.obter();
          // As respostas de pré-requisito são por contexto; guardá-las entre um contexto e
          // outro mostraria "nenhum setor cadastrado" da empresa anterior.
          clearPrerequisiteCache();
          clearOnboardingCache();
          set({ manifesto, escolhaPendente: null, isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            erro: error instanceof Error ? error.message : 'Não foi possível trocar de contexto',
          });
        }
      },

      limpar: () => {
        clearOnboardingCache();
        set({ ...initialState });
      },
    }),
    { name: 'manifesto-store' }
  )
);

/** O acesso da sessão, como as telas consomem. */
export const useManifesto = () => {
  const manifesto = useManifestoStore((estado) => estado.manifesto);
  const escolhaPendente = useManifestoStore((estado) => estado.escolhaPendente);
  const isLoading = useManifestoStore((estado) => estado.isLoading);
  const erro = useManifestoStore((estado) => estado.erro);
  const carregar = useManifestoStore((estado) => estado.carregar);
  const trocarContexto = useManifestoStore((estado) => estado.trocarContexto);

  return { manifesto, escolhaPendente, isLoading, erro, carregar, trocarContexto };
};
